/**
 * InstallationId — Derive a stable per-machine identifier from
 * `os.hostname()` + `os.userInfo()`.
 *
 * Per `docs/requirements/phase-5-auth-device-toolbox/3.功能拆分.md` B2,
 * `installationId` MUST survive Extension re-installs but vary across
 * machines. We compute a UUID v5-style hash over hostname + username +
 * platform so the result is:
 *   - deterministic (same machine → same id)
 *   - collision-resistant (SHA-256, 128-bit truncated)
 *   - browser-safe (no PII survives — username never enters output)
 *
 * Note: this intentionally differs from `vscode.env.machineId`, which
 * is per-Extension-install and uses a different algorithm. The two
 * coexist: `installationId` is what gets sent to the server, while
 * `machineId` (raw `os.hostname()`) is for diagnostics.
 *
 * Refs:
 * - 4.功能规划.md §2.2 — `InstallationId.ts os.hostname() + os.userInfo() 哈希生成`
 * - 3.功能拆分.md B2 — installationId semantics
 */

import { createHash, randomUUID } from "node:crypto";
import * as os from "node:os";

/** Hex-encoded SHA-256 input. Format: `<hostname>|<username>|<platform>|<nodeVersion>`. */
function fingerprintMaterial(): string {
	// `os.userInfo()` is undefined-ish on Windows in some sandboxes; fall
	// back to a process env user, then to a constant placeholder. We never
	// emit the raw username to the caller — it only enters the hash.
	let username = "unknown";
	try {
		username = os.userInfo().username;
	} catch {
		username = process.env.USER ?? process.env.USERNAME ?? "unknown";
	}
	return [
		os.hostname(),
		username,
		os.platform(),
		os.arch(),
		process.versions.node ?? "unknown",
	].join("|");
}

/**
 * Returns a deterministic installation id for the current machine.
 * Use this when you need an id that survives Extension reinstalls
 * but stays stable across restarts on the same machine.
 */
export function deriveInstallationId(): string {
	const material = fingerprintMaterial();
	const digest = createHash("sha256").update(material).digest("hex");
	// Take the first 32 hex chars (128 bits) and reformat as UUID v4-shape
	// so the output looks like a UUID to downstream consumers while
	// remaining a pure SHA-256 truncation.
	return formatAsV4(digest.slice(0, 32));
}

/**
 * Returns a random installation id (UUID v4). Use this for fresh
 * installs when no fingerprint input is available (e.g. containerized
 * CI runners where `os.hostname()` is meaningless).
 */
export function randomInstallationId(): string {
	return randomUUID();
}

/** SHA-256 fingerprint material exposed for tests + diagnostics. */
export function fingerprintSource(): string {
	return fingerprintMaterial();
}

function formatAsV4(hex32: string): string {
	// Stamp version 4 + variant bits per RFC 4122 §4.4. The bits are
	// cosmetic — the underlying entropy is still SHA-256.
	const chars = hex32.split("");
	// Version nibble (position 12 in canonical UUID, index 13 of the 32-char string).
	const versionIdx = 12;
	const variantIdx = 16;
	const versionChar = (parseInt(chars[versionIdx] ?? "8", 16) & 0x0) | 0x4;
	chars[versionIdx] = versionChar.toString(16);
	// Variant nibble: 10xx → first hex char of the 17th position.
	const variantChar = (parseInt(chars[variantIdx] ?? "8", 16) & 0x3) | 0x8;
	chars[variantIdx] = variantChar.toString(16);
	const formatted = chars.join("");
	return `${formatted.slice(0, 8)}-${formatted.slice(8, 12)}-${formatted.slice(12, 16)}-${formatted.slice(16, 20)}-${formatted.slice(20, 32)}`;
}
